import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

interface BookingStatus {
  id: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'failed';
  payment_status: 'pending' | 'completed' | 'failed' | 'refunded';
  confirmation_number: string;
  total_amount: number;
  created_at: string;
  updated_at: string;
}

interface UseRealtimeBookingStatusReturn {
  bookingStatus: BookingStatus | null;
  isLoading: boolean;
  error: string | null;
}

export const useRealtimeBookingStatus = (bookingId: string | null, confirmationNumber?: string | null): UseRealtimeBookingStatusReturn => {
  const [bookingStatus, setBookingStatus] = useState<BookingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBookingStatus = useCallback(async () => {
    if (!bookingId && !confirmationNumber) return;

    try {
      setIsLoading(true);
      setError(null);

      let query = supabase
        .from('reservations')
        .select(`
          id,
          status,
          payment_status,
          confirmation_number,
          total_amount,
          created_at,
          updated_at
        `);

      // Use bookingId if available, otherwise use confirmation_number
      if (bookingId) {
        query = query.eq('id', bookingId);
      } else if (confirmationNumber) {
        query = query.eq('confirmation_number', confirmationNumber);
      }

      const { data, error: fetchError } = await query.single();

      if (fetchError) throw fetchError;

      setBookingStatus(data);
    } catch (err) {
      console.error('Error fetching booking status:', err);
      setError(err instanceof Error ? err.message : 'Failed to load booking status');
    } finally {
      setIsLoading(false);
    }
  }, [bookingId, confirmationNumber]);

  useEffect(() => {
    if (!bookingId && !confirmationNumber) {
      setBookingStatus(null);
      return;
    }

    fetchBookingStatus();

    // Set up real-time subscription for booking status changes
    const identifier = bookingId || confirmationNumber;

    const channel = supabase
      .channel(`booking-status-changes-${identifier}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'reservations',
          filter: bookingId ? `id=eq.${bookingId}` : `confirmation_number=eq.${confirmationNumber}`
        },
        (payload) => {
          setBookingStatus(payload.new as BookingStatus);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchBookingStatus, bookingId, confirmationNumber]);

  return { bookingStatus, isLoading, error };
};
